<template>
  <div class="main-container">
    <div>
      <!-- Header Section -->
      <v-row class="pa-2 header-row">
        <v-col cols="12" class="d-flex justify-space-between align-center">
          <v-btn icon class="back-button" @click="confirmBackNavigation">
            <v-icon class="white--text">mdi-arrow-left</v-icon>
          </v-btn>
          <v-btn 
            v-if="!isTechnician" 
            color="#ff3b47" 
            class="add-job-button my-4 mx-4" 
            dark 
            @click="navigateToAddReport"
          >
            Add Returns
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loader -->
      <v-row v-if="loading" justify="center" align="center" class="loader-row">
        <v-progress-circular indeterminate color="#ff3b47"></v-progress-circular>
      </v-row>

      <!-- Main Content -->
      <div v-if="!loading" class="returns-container">
        <!-- Search and Filter -->
        <v-row justify="center" class="search-filter-row">
          <v-col cols="12" md="4">
            <v-text-field
              class="pa-3 white-search-field"
              filled
              dense
              rounded
              v-model="search"
              append-icon="mdi-magnify"
              label="Search Returns"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedStatus"
              :items="statusOptionsWithClear"
              label="Filter by Status"
              filled
              dense
              rounded
              @change="handleStatusChange"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Returns Cards -->
        <v-row>
          <v-col 
            cols="12" 
            sm="6" 
            md="4" 
            lg="3" 
            v-for="(item, index) in paginatedItems" 
            :key="item.id || index" 
            class="mb-4"
          >
            <v-card class="modern-card" elevation="6" hover>
              <!-- Header with Status and Return ID -->
              <v-row class="d-flex justify-end pr-3 pt-3 align-center">
                <v-chip
                  :color="getChipColor(item.status)"
                  dark
                  label
                  class="status-chip ml-2"
                >
                  {{ item.status || 'Unknown' }}
                </v-chip>
                <v-chip
                  color="grey"
                  dark
                  label
                  class="return-id-chip ml-2"
                >
                  RETURN #: {{ item.returnId || 'N/A' }}
                </v-chip>
                <v-btn
                  icon
                  @click="openExtraInfo(item)"
                  title="Extra Info"
                  class="ml-2"
                >
                  <v-icon color="#ff3b47">mdi-information</v-icon>
                </v-btn>
              </v-row>

              <!-- Card Content -->
              <v-card-text class="pa-4">
                <v-row>
                  <v-col cols="12" class="py-1">
                    <span class="text-caption">Delivery Date:</span>
                    <span class="font-weight-medium">{{ formatDate(item.deliveryDate) || 'N/A' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="py-1">
                    <span class="text-caption">Created Date:</span>
                    <span class="font-weight-medium">{{ formatDate(item.createdDate) || 'N/A' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="py-1">
                    <span class="text-caption">Received By:</span>
                    <span class="font-weight-medium">{{ item.receivedBy || 'N/A' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="py-1">
                    <span class="text-caption">Assigned To:</span>
                    <span class="font-weight-medium">{{ item.assignedTo?.name || 'N/A' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="py-1">
                    <span class="text-caption">Customer:</span>
                    <span class="font-weight-medium">{{ item.customerName || 'N/A' }}</span>
                  </v-col>
                </v-row>
              </v-card-text>

              <!-- Card Actions -->
              <v-card-actions class="d-flex justify-between align-center">
                <v-btn
                  rounded
                  dark
                  color="#ff3b47"
                  @click="isSalesperson ? showDetails(item) : editReturn(item)"
                  class="open-return-button flex-grow-1 mr-2"
                >
                  {{ isSalesperson ? 'Open Return' : 'Edit Return' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Load More Button -->
        <v-row justify="center" v-if="canLoadMore">
          <v-btn
            color="#ff3b47"
            @click="loadMore"
            :disabled="!canLoadMore"
            class="load-more-button"
          >
            Load More
          </v-btn>
        </v-row>
      </div>
    </div>

    <!-- Dialogs -->
    <v-dialog v-model="dialog" max-width="600">
      <v-card>
        <v-card-title class="headline dialog-title">
          Return Details
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title><strong>Return ID:</strong> {{ selectedItem.returnId || 'N/A' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title><strong>Customer Name:</strong> {{ selectedItem.customerName || 'N/A' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title><strong>Invoice Number:</strong> {{ selectedItem.invoiceNumber || 'N/A' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title><strong>Description:</strong> {{ selectedItem.description || 'N/A' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title><strong>Quantity:</strong> {{ selectedItem.quantity || 'N/A' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="green" text @click="goBack">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query,
  where,
  doc, 
  getDoc
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

export default {
  name: "ReturnsComponent",
  data() {
    return {
      loading: true,
      items: [],
      search: '',
      selectedStatus: '',
      statusOptions: [
        "Booked",
        "Inspection",
        "CreditApproval",      
        "LabelsAndPackaging",
        "MoveToStores",
        "Rejected"
      ],
      dialog: false,
      backDialog: false,
      selectedItem: {},
      userRole: '',
      userId: null,
      userEmail: null,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      visibleCount: 12,
      loadIncrement: 12
    };
  },
  computed: {
    statusOptionsWithClear() {
      return ['Clear Filtering', ...this.statusOptions];
    },
    isSalesperson() {
      return this.userRole === 'Salesperson';
    },
    isTechnician() {
      return this.userRole === 'Technician';
    },
    filteredItems() {
      let filtered = this.items;
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(item => 
          item.returnId.toString().includes(searchLower) ||
          (item.customerName && item.customerName.toLowerCase().includes(searchLower)) ||
          (item.invoiceNumber && item.invoiceNumber.toLowerCase().includes(searchLower)) ||
          (item.referenceNumbers && item.referenceNumbers.some(refnum => refnum.toLowerCase().includes(searchLower)))
        );
      }
      if (this.selectedStatus && this.selectedStatus !== 'Clear Filtering') {
        filtered = filtered.filter(item => item.status === this.selectedStatus);
      }
      return filtered.filter(item => item.status !== "Completed")
                    .sort((a, b) => b.returnId - a.returnId);
    },
    paginatedItems() {
      return this.filteredItems.slice(0, this.visibleCount);
    },
    canLoadMore() {
      return this.visibleCount < this.filteredItems.length;
    }
  },
  methods: {
    async fetchUserRole() {
      try {
        const user = auth.currentUser;
        if (user) {
          this.userId = user.uid;
          this.userEmail = user.email;
          const userDoc = await getDoc(doc(db, "users", user.uid));
          this.userRole = userDoc.exists() ? userDoc.data().role : "Guest";
          localStorage.setItem("role", this.userRole);
        }
      } catch (error) {
        console.error("Error fetching role:", error);
      }
    },
    async fetchReports() {
      try {
        const q = query(
          collection(db, "returns"),
          where("assignedTo.email", "==", this.userEmail)
        );
        
        const querySnapshot = await getDocs(q);
        this.items = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            returnId: data.returnId,
            customerName: data.customer?.companyName,
            invoiceNumber: data.invoiceNumber,
            description: data.returnReason,
            quantity: data.returnItems?.reduce((acc, item) => acc + parseInt(item.qty, 10), 0),
            status: data.returnStatus,
            deliveryDate: data.deliveryDate,
            createdDate: data.createdDate,
            receivedBy: data.receivedBy,
            assignedTo: data.assignedTo || { name: 'N/A', email: '' },
            referenceNumbers: data.returnItems?.map(item => item.refnum).filter(Boolean) || []
          };
        });
      } catch (error) {
        console.error("Error fetching returns:", error);
        this.showSnackbar("Failed to fetch returns", "error");
      } finally {
        this.loading = false;
      }
    },
    showDetails(item) {
      this.selectedItem = item;
      this.dialog = true;
    },
    editReturn(item) {
      this.$router.push({ name: 'EditReturns', params: { id: item.id } });
    },
    navigateToAddReport() {
      this.$router.push({ name: 'AddReturns' });
    },
    confirmBackNavigation() {
      this.backDialog = true;
    },
    goBack() {
      this.backDialog = false;
      this.$router.go(-1);
    },
    loadMore() {
      this.visibleCount += this.loadIncrement;
    },
    getChipColor(status) {
      const statusMap = {
        'booked': 'blue',
        'inspection': 'orange',
        'creditaapproval': 'purple',
        'labelsandpackaging': 'pink',
        'movetostores': 'brown',
        'completed': 'green',
        'rejected': 'red'
      };
      return statusMap[status?.toLowerCase()] || 'grey';
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return isNaN(date) ? dateStr : date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    showSnackbar(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    handleStatusChange() {
      if (this.selectedStatus === 'Clear Filtering') this.selectedStatus = '';
    }
  },
  async created() {
    await this.fetchUserRole();
    await this.fetchReports();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

.main-container {
  background-color: #f4f6f9;
  min-height: 100vh;
  padding: 16px;
}

.header-row {
  background-color: #1e1e1e;
  border-bottom: 1px solid #333;
}

.returns-container {
  background-color: #ffffff;
  border-radius: 20px 20px 0 0;
  padding: 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.modern-card {
  background-color: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  margin: 5px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  height: 260px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.modern-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.status-chip {
  font-size: 0.75rem;
  text-transform: capitalize;
}

.return-id-chip {
  font-size: 0.75rem;
  text-transform: uppercase;
}

.open-return-button {
  font-weight: 600;
  transition: all 0.3s ease;
}

.open-return-button:hover {
  background-color: #e63946 !important;
  transform: scale(1.02);
}

.text-caption {
  font-size: 0.8rem;
  color: #757575;
}

.font-weight-medium {
  font-weight: 500;
  color: #333;
}

.load-more-button {
  font-weight: 600;
  margin-top: 24px;
  transition: all 0.3s ease;
}

.load-more-button:hover {
  transform: scale(1.05);
}

.white-search-field .v-input__control {
  background-color: white !important;
}

.dialog-title {
  background-color: #ff3b47;
  color: white !important;
}

@media (max-width: 600px) {
  .modern-card {
    margin: 12px;
    height: 350px;
  }
  
  .v-col {
    margin-bottom: 20px;
  }
}
</style>